import type { DocumentDTO } from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useEffect, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { PropertyChat } from "../../components/chat/property-chat"
import { ApiError } from "../../lib/api-client"
import { DocumentAiReviewModal } from "../components/documents/document-ai-review-modal"
import { DocumentEditModal } from "../components/documents/document-edit-modal"
import { DocumentGrantsModal } from "../components/documents/document-grants-modal"
import { DocumentList } from "../components/documents/document-list"
import { DocumentUploadModal } from "../components/documents/document-upload-modal"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { PageHeader } from "../components/page-header"
import { PropertyBottomNav } from "../components/property-bottom-nav"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { usePropertyDocuments } from "../hooks/use-documents"
import { useLastPropertyId } from "../hooks/use-last-property-id"
import { useProjects } from "../hooks/use-projects"
import { useProperty } from "../hooks/use-property"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

function PlusIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			className="w-5 h-5"
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

function DocumentsIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-10 h-10"
			aria-hidden="true"
		>
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
			<polyline points="14 2 14 8 20 8" />
			<line x1="16" y1="13" x2="8" y2="13" />
			<line x1="16" y1="17" x2="8" y2="17" />
			<polyline points="10 9 9 9 8 9" />
		</svg>
	)
}

export function PropertyDocumentsPage() {
	const { id } = useParams<{ id: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const documents = usePropertyDocuments(id ?? "")
	const { data: projects } = useProjects(id)
	const { setLastPropertyId } = useLastPropertyId()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const td = t.projects.documents
	const [shareOpen, setShareOpen] = useState(false)
	const [uploadOpen, setUploadOpen] = useState(false)
	const [grantsDoc, setGrantsDoc] = useState<DocumentDTO | null>(null)
	const [editingDoc, setEditingDoc] = useState<DocumentDTO | null>(null)
	const [reviewingDoc, setReviewingDoc] = useState<DocumentDTO | null>(null)

	const isNotFound = !id || (error instanceof ApiError && error.status === 404)

	useEffect(() => {
		if (property) setLastPropertyId(property.id)
		else if (isNotFound) setLastPropertyId(null)
	}, [property, isNotFound, setLastPropertyId])

	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	if (error) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-destructive px-6 text-center">
				{(error as ApiError).message}
			</div>
		)
	}
	if (!property) return null

	const scope = { type: "property" as const, propertyId: property.id }
	const items = documents.data ?? []

	const projectNameById = new Map((projects ?? []).map((p) => [p.id, p.name]))
	function scopeLabelFor(doc: DocumentDTO): string | null {
		if (doc.scope.type === "property") return td.scopeProperty
		if (doc.scope.type === "project") {
			return projectNameById.get(doc.scope.id) ?? td.scopeProject
		}
		return null
	}

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
			/>
			<PageContainer>
				<PageActionsBar>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
					/>
				</PageActionsBar>
				<PageHeader
					eyebrow={property.name}
					eyebrowHref={webPaths.property(property.id)}
					title={td.allInProperty}
					meta={
						<>
							{items.length > 0 ? (
								<button
									type="button"
									onClick={() => setUploadOpen(true)}
									className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
								>
									<PlusIcon />
									{td.addCta}
								</button>
							) : null}
						</>
					}
				/>

				{documents.isLoading ? (
					<div className="py-12 text-center text-sm text-muted-foreground">
						{t.common.loading}
					</div>
				) : items.length === 0 && !documents.isError ? (
					<div className="rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 flex flex-col items-center text-center">
						<div className="text-muted-foreground/70 mb-3">
							<DocumentsIcon />
						</div>
						<h2 className="text-base font-bold text-foreground">
							{td.emptyTitle}
						</h2>
						<p className="mt-1.5 text-sm text-muted-foreground/80 max-w-sm">
							{td.emptyBodyProperty}
						</p>
						<button
							type="button"
							onClick={() => setUploadOpen(true)}
							className="mt-6 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
						>
							<PlusIcon />
							{td.addCta}
						</button>
					</div>
				) : (
					<DocumentList
						documents={items}
						scope={scope}
						onOpenGrants={setGrantsDoc}
						onEdit={setEditingDoc}
						onReviewAiClick={setReviewingDoc}
						error={documents.isError ? documents.error : undefined}
						scopeLabelFor={scopeLabelFor}
					/>
				)}
			</PageContainer>
			<PropertyBottomNav propertyId={property.id} />
			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
			{uploadOpen ? (
				<DocumentUploadModal
					propertyId={property.id}
					projects={projects ?? []}
					onClose={() => setUploadOpen(false)}
				/>
			) : null}
			{grantsDoc ? (
				<DocumentGrantsModal
					propertyId={property.id}
					document={grantsDoc}
					onClose={() => setGrantsDoc(null)}
				/>
			) : null}
			{editingDoc ? (
				<DocumentEditModal
					propertyId={property.id}
					scope={scope}
					document={editingDoc}
					onClose={() => setEditingDoc(null)}
				/>
			) : null}
			{reviewingDoc ? (
				<DocumentAiReviewModal
					propertyId={property.id}
					document={reviewingDoc}
					onClose={() => setReviewingDoc(null)}
				/>
			) : null}
			<PropertyChat propertyId={property.id} />
		</div>
	)
}
